import React, { useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Logo } from "@/components/Logo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { CircleNotch, Envelope, CheckCircle, ArrowLeft } from "@phosphor-icons/react";
import { toast } from "sonner";

const BG_IMAGE =
  "https://images.unsplash.com/photo-1631812314418-d2c27f4d85ee?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzl8MHwxfHNlYXJjaHwxfHxhbWFsZmklMjBjb2FzdCUyMHN1bnNldHxlbnwwfHx8fDE3NzY5ODg2NTR8MA&ixlib=rb-4.1.0&q=85";

export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [deliveryFailed, setDeliveryFailed] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setDeliveryFailed(false);
    try {
      const { data } = await api.post("/auth/forgot-password", { email });
      if (data?.delivery === "failed") {
        setDeliveryFailed(true);
        toast.error(data.message || "Could not send reset email");
      } else {
        setSent(true);
      }
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not send reset email");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen relative overflow-hidden bg-ink text-white grain">
      <div className="absolute inset-0">
        <img src={BG_IMAGE} alt="" className="w-full h-full object-cover opacity-40" />
        <div className="absolute inset-0 bg-gradient-to-br from-black/90 via-black/70 to-black/90" />
      </div>

      <div className="relative z-10 min-h-screen flex flex-col">
        <div className="p-8">
          <Logo size="default" />
        </div>

        <div className="flex-1 flex items-center justify-center px-6">
          <div className="w-full max-w-md">
            {sent ? (
              <div data-testid="forgot-sent-state">
                <div className="w-14 h-14 rounded-full bg-[#D4AF37]/15 border border-[#D4AF37]/40 flex items-center justify-center mb-6">
                  <CheckCircle size={28} className="text-[#D4AF37]" weight="duotone" />
                </div>
                <p className="text-[11px] uppercase tracking-[0.35em] text-[#D4AF37] mb-4 font-sans">
                  Check your inbox
                </p>
                <h1 className="font-serif text-4xl sm:text-5xl tracking-tight text-white mb-4">
                  We've sent you a <span className="italic text-[#D4AF37]">reset link</span>
                </h1>
                <p className="text-white/70 text-sm font-sans leading-relaxed mb-4">
                  If <span className="text-white">{email}</span> is on our system, a password reset email is on its way. The link expires in <strong className="text-[#D4AF37]">60 minutes</strong> and can only be used once.
                </p>
                <div className="rounded-md border border-[#D4AF37]/25 bg-[#D4AF37]/5 p-3 mb-8" data-testid="forgot-spam-hint">
                  <p className="text-[12px] text-white/70 leading-relaxed">
                    <strong className="text-[#D4AF37]">Don't see the email after 5 minutes?</strong>
                    {" "}Check your <strong>Spam</strong> or <strong>Promotions</strong> folder, or contact{" "}
                    <a href="mailto:info@besttimetravelandtours.com" className="text-[#D4AF37] hover:text-[#F3C853]">info@besttimetravelandtours.com</a>{" "}
                    for help.
                  </p>
                </div>
                <Link
                  to="/login"
                  data-testid="forgot-back-to-login"
                  className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.3em] text-white/60 hover:text-[#D4AF37] transition-colors font-sans"
                >
                  <ArrowLeft size={14} />
                  Back to login
                </Link>
              </div>
            ) : (
              <>
                <div className="mb-10">
                  <p className="text-[11px] uppercase tracking-[0.35em] text-[#D4AF37] mb-4 font-sans">
                    Password Reset
                  </p>
                  <h1 className="font-serif text-4xl sm:text-5xl tracking-tight text-white mb-3">
                    Forgot your <span className="italic text-[#D4AF37]">password</span>?
                  </h1>
                  <p className="text-white/60 text-sm font-sans">
                    Enter your email and we'll send you a secure link to set a new one.
                  </p>
                </div>

                <form onSubmit={onSubmit} className="space-y-5" data-testid="forgot-password-form">
                  {deliveryFailed && (
                    <div className="rounded-md border border-red-400/40 bg-red-500/10 p-4" data-testid="forgot-delivery-failed">
                      <p className="text-[11px] uppercase tracking-widest text-red-300 mb-1.5 font-sans">Email could not be sent</p>
                      <p className="text-sm text-white/80 leading-relaxed">
                        We're unable to send password reset emails right now. Please contact{" "}
                        <a href="mailto:info@besttimetravelandtours.com" className="text-[#D4AF37] hover:text-[#F3C853]">info@besttimetravelandtours.com</a>{" "}
                        and we'll reset your password directly.
                      </p>
                    </div>
                  )}
                  <div>
                    <Label htmlFor="email" className="text-xs uppercase tracking-widest text-white/60 mb-2 block">
                      Email
                    </Label>
                    <div className="relative">
                      <Envelope size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40" weight="duotone" />
                      <Input
                        id="email"
                        type="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        required
                        autoComplete="email"
                        placeholder="you@example.com"
                        className="pl-10 h-12 bg-black/40 border-white/10 text-white focus:border-[#D4AF37] focus:ring-[#D4AF37]/20 font-sans"
                        data-testid="forgot-email-input"
                      />
                    </div>
                  </div>

                  <Button
                    type="submit"
                    disabled={loading || !email}
                    className="w-full h-12 bg-[#D4AF37] hover:bg-[#F3C853] text-black font-medium text-sm uppercase tracking-[0.25em] transition-all duration-200 hover:-translate-y-[1px]"
                    data-testid="forgot-submit-btn"
                  >
                    {loading ? <CircleNotch size={18} className="animate-spin" /> : "Send reset link"}
                  </Button>

                  <div className="text-center pt-2">
                    <Link
                      to="/login"
                      data-testid="forgot-back-link"
                      className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.3em] text-white/50 hover:text-[#D4AF37] transition-colors font-sans"
                    >
                      <ArrowLeft size={14} />
                      Back to login
                    </Link>
                  </div>
                </form>
              </>
            )}
          </div>
        </div>

        <div className="p-6 text-center text-[11px] uppercase tracking-[0.3em] text-white/30 font-sans">
          A curated concierge experience
        </div>
      </div>
    </div>
  );
}
